import { useForm, Controller } from "react-hook-form";
import { validatePhoneNumber } from "../../../customer/validators";
import Button from "../../../../common/components/xepp/button/Button";
import styles from "./AddStaffModal.module.scss";
import styles2 from './../ReferralSystem.module.scss'
import { CloseIcon } from "../../../../assets/icons";
import Input from "../../../../common/components/input-service-request/Input";
import PhoneInput from "../../../../common/ekedc-components/phone-number-input/PhoneInput";
import FileInput from "./DisplayInputFile";
import DisplayInputFile from "./DisplayInputFile";
import InputFile from "./InputFile";

type UploadType = "Single" | "Bulk";

type Props = {
    onClose: () => void;
    onSubmit: (data: StaffFormData) => void;
    isLoading?: boolean;
};

export interface StaffFormData {
    uploadType: UploadType;
    name: string;
    email: string;
    staffId: string;
    phoneNumber: string;
    file: File | null;
}

const AddStaffModal = ({ onClose, onSubmit, isLoading = false }: Props) => {
    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<StaffFormData>({
        defaultValues: {
            uploadType: "Single",
            name: "",
            email: "",
            staffId: "",
            phoneNumber: "",
            file: null,
        },
    });

    const uploadType = watch("uploadType");

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleSubmit_ = (data: StaffFormData) => {
        onSubmit({
            ...data,
            name: data.name?.trim(),
            email: data.email?.trim(),
            staffId: data.staffId?.trim(),
        });
    };

    return (
        <div className={styles.backdrop} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <h2 className={styles.title}>Add Staff</h2>
                        <p className={styles.titleDescription}>Select upload type</p>
                    </div>
                    <button className={styles.closeButton} onClick={onClose}><CloseIcon /></button>
                </div>

                <form onSubmit={handleSubmit(handleSubmit_)}>
                    <div className={styles.body}>
                        <div className={styles.uploadTypeWrapper}>
                            <span className={styles.uploadTypeLabel}>Upload Type</span>
                            <div className={styles.tabSwitch}>
                                <button
                                    type="button"
                                    className={`${styles.tabBtn} ${uploadType === "Single" ? styles.active : ""}`}
                                    onClick={() => setValue("uploadType", "Single")}
                                >
                                    Single
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.tabBtn} ${uploadType === "Bulk" ? styles.active : ""}`}
                                    onClick={() => setValue("uploadType", "Bulk")}
                                >
                                    Bulk
                                </button>
                            </div>
                        </div>

                        <hr className={styles.divider} />

                        {
                            uploadType == 'Single' ?
                                <>
                                    <Input
                                        label="Full name"
                                        placeholder="eg. John"
                                        inputSize="large"
                                        error={errors.name?.message}
                                        {...register("name", {
                                            validate: (val) => {
                                                if (uploadType !== "Single") return true;
                                                if (!val) return "Name is required";
                                                if (!/^[a-zA-Z\s]+$/.test(val)) return "Name can only contain letters and spaces";
                                                return true;
                                            },
                                        })}
                                    />
                                    <Input
                                        label="Email Address"
                                        placeholder="eg. example@gmail.com"
                                        inputSize="large"
                                        error={errors.email?.message}
                                        {...register("email", {
                                            validate: (val) => {
                                                if (uploadType !== "Single") return true;
                                                if (!val) return "Email is required";
                                                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Enter a valid email address";
                                                return true;
                                            },
                                        })}
                                    />

                                    <hr className={styles.divider} />

                                    <Input
                                        label="Staff ID"
                                        placeholder="Enter staff ID"
                                        inputSize="large"
                                        error={errors.staffId?.message}
                                        {...register("staffId", {
                                            validate: (val) => {
                                                if (uploadType !== "Single") return true;
                                                if (!val) return "Staff ID is required";
                                                if (!/^[a-zA-Z0-9]+$/.test(val)) return "Staff ID can only contain letters and numbers";
                                                return true;
                                            },
                                        })}
                                    />
                                    <Controller
                                        name="phoneNumber"
                                        control={control}
                                        rules={{
                                            validate: (val) => {
                                                if (uploadType !== "Single") return true;
                                                if (!val) return "Phone number is required";
                                                return validatePhoneNumber(val);
                                            },
                                        }}
                                        render={({ field }) => (
                                            <PhoneInput
                                                label="Phone Number"
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                placeholder="Enter phone number"
                                                error={errors.phoneNumber?.message}
                                                inputSize="large"
                                                disableCountrySelector
                                            />
                                        )}
                                    />
                                </> : <>



                                    <DisplayInputFile title="Template XLSX file" description="Download the sample file here to populate the staff data." fileName="example.xlsx" />

                                    <Controller
                                        name="file"
                                        control={control}
                                        rules={{ validate: (val) => uploadType !== "Bulk" || !!val || "File is required" }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <InputFile onChange={field.onChange} />
                                                {fieldState.error && (
                                                    <p className={styles.errorText}>{fieldState.error.message}</p>
                                                )}
                                            </>
                                        )}
                                    />
                                </>}
                    </div>

                    <div className={styles.footer}>
                        <Button text="Cancel Setup" variant="outline" onClick={onClose} />
                        <Button text={isLoading ? "Adding..." : "Add Staff"} type="submit" disabled={isLoading} className={styles2.btn} />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddStaffModal;
